import {formatDate, formatPercent} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbDrillRowView} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {AdminDashboardReportBlock, AdminDashboardReportProvider, AdminDashboardReportRow} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {AdminDashboardDrillView} from '@features/admin-panel/models/admin-dashboard-drill.models';
import {AdminDashboardCohortDrill} from '@features/admin-panel/models/admin-dashboard-analysis.models';
import {AdminDashboardDrillService} from '../drill/admin-dashboard-drill.service';
import {
  AdminDashboardCohortCell,
  AdminDashboardCohortRow,
  AdminDashboardQueryParams,
  AdminDashboardScope,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';

/** What a cohort cell shows: a shaded share, a "from <day>" placeholder, or a dash (no history / nobody joined). */
export type AdminDashboardCohortCellKind = 'value' | 'pending' | 'nohistory' | 'empty';

/**
 * Cohort retention: for each of the 12 complete join months before this one, the share of the companies that joined
 * then which logged in 1, 3 and 6 months later. A cell still collecting history shows the day it fills; a cell whose
 * window starts before the login history begins shows a dash. The period does not apply. Reloads whenever the
 * dashboard's query params change (scope and filters). A value cell opens the drill of who dropped off (ITP-E-161-05).
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-cohort-retention',
  templateUrl: './admin-dashboard-cohort-retention.component.html',
  styleUrls: ['./admin-dashboard-cohort-retention.component.scss']
})
export class AdminDashboardCohortRetentionComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  @Input() scope: AdminDashboardScope = 'Clients';
  @Input() params: AdminDashboardQueryParams;
  /** A company picked with Evidence in the drill (ITP-E-161-05): the page opens its why panel. */
  @Output() companySelected = new EventEmitter<string>();
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  rows: AdminDashboardCohortRow[] = [];
  /** Companies across the 12 cohorts. */
  companies = 0;
  /** True when at least one cell has no login history behind it. */
  hasNoHistory = false;
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;
  readonly failedTitle = 'Couldn\'t load cohort retention';

  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry,
    private host: ElementRef<HTMLElement>, private drillService: AdminDashboardDrillService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('deeper-analysis', this.host.nativeElement, this);
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-11). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    const title = 'Cohort retention';
    if (this.failed) {
      return [{kind: 'empty', title, text: this.failedTitle}];
    }
    const subtitle = 'Companies still active 1, 3 and 6 months after joining, by join month — the last 12 months, whatever the period. '
      + `${countOf(this.companies, 'company', 'companies')}.`;
    if (this.companies <= 0) {
      return [{kind: 'empty', title, subtitle, text: 'No companies joined in the last 12 months in this view'}];
    }
    return [{
      kind: 'table',
      title,
      subtitle,
      columns: [
        {header: 'Joined', weight: 1.4},
        {header: 'Companies', align: 'right'},
        {header: 'Month 1', align: 'right'},
        {header: 'Month 3', align: 'right'},
        {header: 'Month 6', align: 'right'}
      ],
      rows: this.rows.map((row): AdminDashboardReportRow => ({
        cells: [row.label, String(row.size), ...(row.cells ?? []).map(cell => this.cellText(cell))]
      })),
      note: 'Collecting history — a cell fills once every company in the cohort has reached that month.'
        + (this.hasNoHistory ? ' – No login history that far back yet.' : '')
    }];
  }

  /** What a cell shows on screen: "50%", "from 28 Aug", or a dash (U+2013). */
  private cellText(cell: AdminDashboardCohortCell): string {
    switch (this.cellKind(cell)) {
      case 'value':
        return formatPercent(cell.value, 'en-US', '1.0-0');
      case 'pending':
        return `from ${formatDate(cell.availableFrom, 'd MMM', 'en-US', 'UTC')}`;
      default:
        return '–';
    }
  }

  cellKind(cell: AdminDashboardCohortCell): AdminDashboardCohortCellKind {
    if (cell.value !== null && cell.value !== undefined) {
      return 'value';
    }
    if (cell.noHistory) {
      return 'nohistory';
    }
    return cell.availableFrom ? 'pending' : 'empty';
  }

  /** The tile strength of a value cell, 0.1 (0%) to 1 (100%) like the mockup's heatmap; the stylesheet reads it as --cohort-alpha. */
  alpha(value: number): number {
    return Math.round((0.1 + 0.9 * value) * 100) / 100;
  }

  cellTitle(row: AdminDashboardCohortRow, cell: AdminDashboardCohortCell): string {
    switch (this.cellKind(cell)) {
      case 'value':
        return `${Math.round(cell.value * 100)}% still active ${cell.offset} month${cell.offset > 1 ? 's' : ''} after joining (${cell.retained} of ${row.size})`
          + '\nClick to list who dropped off';
      case 'pending':
        return `Collecting history — month ${cell.offset} available from ${formatDate(cell.availableFrom, 'd MMM yyyy', 'en-US', 'UTC')}`;
      case 'nohistory':
        return 'No login history this far back yet';
      default:
        return 'No companies joined this month';
    }
  }

  /**
   * A value cell was clicked (H2, ITP-E-161-05): the drill lists the cohort's members with no login in that month's
   * 30-day window. Pending, no-history and empty cells do nothing. Evidence on a row emits companySelected.
   */
  openDrill(row: AdminDashboardCohortRow, cell: AdminDashboardCohortCell): void {
    if (!this.params || this.cellKind(cell) !== 'value') {
      return;
    }
    const offset = cell.offset;
    this.drillService.open({
      title: `${row.label} cohort — not active in month ${offset}`,
      columns: [{header: 'Joined', align: 'end'}, {header: 'Source', align: 'end'}],
      load: () => this.loadDrill(row, offset)
    }).subscribe(result => {
      if (result?.action === 'evidence') {
        this.companySelected.emit(result.companyId);
      }
    });
  }

  /** Enter or Space on a focused value cell opens its drill, like a click. */
  onCellKeydown(event: KeyboardEvent, row: AdminDashboardCohortRow, cell: AdminDashboardCohortCell): void {
    if ((event.key !== 'Enter' && event.key !== ' ') || this.cellKind(cell) !== 'value') {
      return;
    }
    event.preventDefault();
    this.openDrill(row, cell);
  }

  /** The drill's rows: "Ann Lee · 12 seats" under each name, then Joined (d MMM yyyy) and Source ("–" when not recorded). */
  private loadDrill(row: AdminDashboardCohortRow, offset: number): Observable<AdminDashboardDrillView> {
    return this.dataService.getWithExtra<AdminDashboardCohortDrill>('AdminDashboardCohortDrillQuery', this.params, {
      Month: formatDate(row.month, 'yyyy-MM', 'en-US', 'UTC'),
      Offset: offset
    }).pipe(map(drill => {
      const rows = (drill?.Rows ?? []).map(member => adbDrillRowView(member, [
        {text: formatDate(member.joined, 'd MMM yyyy', 'en-US', 'UTC')},
        {text: member.source || '–'}
      ]));
      const size = drill?.CohortSize ?? row.size;
      const dropped = drill?.Dropped ?? rows.length;
      const subtitle = drill?.Pending
        ? `Collecting history — month ${offset} is not available yet.`
        : `${countOf(dropped, 'company', 'companies')} of ${size} had no logins ${offset} month${offset > 1 ? 's' : ''} after joining.`;
      return {subtitle, rows, total: drill?.Total ?? rows.length};
    }));
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardCohortRow[]>(
      'AdminDashboardCohortRetentionQuery', this.params, 'Cohorts'
    ).subscribe({
      next: rows => this.show(Array.isArray(rows) ? rows : [], false),
      error: () => this.show([], true)
    });
  }

  private show(rows: AdminDashboardCohortRow[], failed: boolean): void {
    this.rows = rows;
    this.companies = rows.reduce((sum, row) => sum + (row.size ?? 0), 0);
    this.hasNoHistory = rows.some(row => (row.cells ?? []).some(cell => this.cellKind(cell) === 'nohistory'));
    this.failed = failed;
    this.loaded = true;
    this.report(failed ? 'failed' : this.companies > 0 ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }
}
